import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import {
  ControlLabel,
  FormControl,
  FormGroup,
  HelpBlock,
  InputGroup,
  Modal,
} from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { Category } from '../../../../../shared/issuesV2';
import { PolicyType } from '../../../../../typings/enums';
import { userHasPermissionToCreatePolicyType } from '../../../../helpers/permissions';
import Button from '../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import ButtonGroup from '../../../generic/Button/ButtonGroup';
import InlineLoaderButton from '../../../generic/Button/InlineLoaderButton';
import CopyToClipboard from '../../../generic/CopyToClipboard';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import LabeledCheckbox from '../../../generic/Forms/LabeledCheckbox';
import TextInput from '../../../generic/Forms/TextInput';
import QualityRequired from '../../../generic/Gates/QualityRequired';
import SecurityRequired from '../../../generic/Gates/SecurityRequired';
import Icon, { icons } from '../../../generic/Icon';
import { DocsLink, RouterLink } from '../../../generic/Link';
import Loader from '../../../generic/Loader';
import usePolicies from '../../../shared/policies/hooks/usePolicies';
import type {
  APITokenCreateRequest,
  APITokenResponse,
  APITokenUpdateRequest,
} from '../../../../../shared/settings/apiTokens';

import styles from './apiTokens.module.less';

export type ModalMode = {
  token: APITokenResponse;
  mode: 'Edit';
} |
{
  token: null;
  mode: 'Create';
} |
{
  token: null;
  mode: 'Hide';
};

type ModalCreateOrEditTokenProps = {
  mode: ModalMode;
  isLoading: boolean;
  errors: string[] | null;
  onHide: () => void;
  onCreateToken: (token: APITokenCreateRequest) => Promise<string | null>;
  onUpdateToken: (tokenId: number, token: APITokenUpdateRequest) => Promise<void>;
};

const getName = (t: APITokenResponse | null) => t?.name || '';
const getLicensingPolicy = (t: APITokenResponse | null) => t?.defaultLicensingPolicyId || null;
const getSecurityPolicy = (t: APITokenResponse | null) => t?.defaultSecurityPolicyId || null;
const getQualityPolicy = (t: APITokenResponse | null) => t?.defaultQualityPolicyId || null;
const getIsPushOnly = (t: APITokenResponse | null) => t?.isPushOnly ?? true;

type NewlyAddedTokenProps = {
  token: string;
  onConfirm: () => void;
};

const NewlyAddedToken: VoidFunctionComponent<NewlyAddedTokenProps> = ({
  token,
  onConfirm,
}) => {
  return (
    <div className={styles.newTokenDisplay}>
      <div>
        <h1>Token successfully generated</h1>
        <p>Please copy this token. It will never be displayed again</p>
      </div>
      <div className={styles.tokenLabel}>
        <TextInput
          label="token-label"
          value={token}
          suffix={<CopyToClipboard text={token} />}
        />
      </div>
      <div className={styles.confirmButton}>
        <Button onClick={onConfirm} size={BUTTON_SIZE.LARGE} type={BUTTON_TYPE.PRIMARY}>Ok</Button>
      </div>
    </div>
  );
};

const ModalCreateOrEditToken: VoidFunctionComponent<ModalCreateOrEditTokenProps> = ({
  mode: {
    mode,
    token,
  },
  isLoading,
  errors,
  onHide,
  onCreateToken,
  onUpdateToken,
}) => {
  const [newKeyName, setNewKeyName] = useState(getName(token));
  const [defaultPolicyId, setDefaultLicensingPolicy] =
    useState<number | null>(getLicensingPolicy(token));
  const [defaultSecurityPolicyId, setDefaultSecurityPolicy] =
    useState<number | null>(getSecurityPolicy(token));
  const [defaultQualityPolicyId, setDefaultQualityPolicy] =
    useState<number | null>(getQualityPolicy(token));
  const [isPushOnly, setPushOnly] = useState(getIsPushOnly(token));

  const [newlyAddedToken, setNewlyAddedToken] = useState<string | null>(null);

  const shouldShowModal = mode !== 'Hide';

  const { policies, isLoading: arePoliciesLoading, errors: policyErrors } = usePolicies();

  const allErrors = [
    ...errors ?? [],
    ...policyErrors ?? [],
  ];

  useEffect(() => {
    setNewKeyName(getName(token));
    setDefaultLicensingPolicy(getLicensingPolicy(token));
    setDefaultSecurityPolicy(getSecurityPolicy(token));
    setDefaultQualityPolicy(getQualityPolicy(token));
    setPushOnly(getIsPushOnly(token));
  }, [token]);

  const policyOptionsOfType = useCallback((category: Category) =>
    (policies[category])
      .filter((policy) => policy.value !== null)
      .map((policy) => (
        <option key={policy.value} value={policy.value?.toString()}>
          {policy.label}
        </option>
      )), [policies]);

  const onCreateNewToken = async () => {
    const newToken = await onCreateToken({
      name: newKeyName || '',
      pushOnly: isPushOnly.toString(),
      meta: {
        defaultPolicyId,
        defaultSecurityPolicyId,
        defaultQualityPolicyId,
      },
    });
    if (newToken) {
      setNewlyAddedToken(newToken);
    }
  };

  const createOrUpdateToken = async () => {
    if (mode === 'Edit') {
      await onUpdateToken(token.id, {
        name: newKeyName || '',
        meta: {
          defaultPolicyId,
          defaultSecurityPolicyId,
          defaultQualityPolicyId,
        },
      // if mode is 'Edit', then token is non null
      });
      onHide();
      return;
    }

    await onCreateNewToken();
  };

  const onHideModal = () => {
    onHide();
    setNewKeyName('');
    setDefaultLicensingPolicy(null);
    setDefaultSecurityPolicy(null);
    setDefaultQualityPolicy(null);
    setNewlyAddedToken(null);
  };

  return (
    <Modal
      className="modal-horizontal"
      bsSize="large"
      show={shouldShowModal}
      onHide={onHideModal}
    >
      <div className="row">
        <div className="col-md-3 modal-horizontal-header">
          <div className="padded">
            <span className="icon-feature">
              <Icon icon={icons.addToProject} />
            </span>
            <h4>{`${mode}`} an API Token</h4>
          </div>
        </div>
        {!_.isEmpty(allErrors) && <ErrorAlert status={{ errors: allErrors }} />}
        <div className="col-md-9 modal-horizontal-content">
          {newlyAddedToken
            ? <NewlyAddedToken token={newlyAddedToken} onConfirm={onHideModal} />
            : (
              <div className="padded">
                <FormGroup
                  controlId="repoInputForm"
                  style={{ display: 'flex', flexDirection: 'column' }}
                >
                  <InputGroup className="input-group input-group-md">
                    <ControlLabel className="form-label">Name</ControlLabel>
                    <FormControl
                      type="text"
                      bsSize="large"
                      className="input-repo-url input-block-level"
                      onChange={(e) => setNewKeyName((e.target as HTMLTextAreaElement).value)}
                      value={newKeyName}
                      placeholder="My API token name"
                    />
                  </InputGroup>
                  <InputGroup
                    className="input-group input-group-md"
                    style={{ marginTop: 30 }}
                  >
                    <LabeledCheckbox
                      label="Push Only"
                      isChecked={isPushOnly}
                      onChange={() => {
                        setPushOnly(!isPushOnly);
                      }}
                      isDisabled={!!token}
                      isRippleDisabled
                    />
                    <HelpBlock>
                      Push only tokens can only be used to send data to FOSSA.
                      Select this option if you only plan to use the API token with{' '}
                      <DocsLink to="provided-builds">Provided Builds</DocsLink>. Do{' '}
                      <strong>not</strong> select this option if you want to have
                      full access to the FOSSA API.{' '}
                      <strong>Once created, tokens cannot be changed.</strong>
                    </HelpBlock>
                  </InputGroup>
                  {mode === 'Edit' && arePoliciesLoading &&
                    <Loader />}
                  {mode === 'Edit' && !arePoliciesLoading &&
                    <div>
                      <h3>
                        Select associated policy <small>(optional)</small>
                      </h3>
                      <HelpBlock>
                        Select the default policy that should be used for all new
                        projects created by fossa-cli using this API key. If a
                        policy is not set for this key, your organization default
                        will be used.
                      </HelpBlock>
                      <InputGroup
                        className="input-group input-group-md"
                        style={{ marginTop: 20 }}
                      >
                        <ControlLabel className="form-label">
                          Default Licensing Policy
                          {userHasPermissionToCreatePolicyType(PolicyType.LICENSING) && (
                            <RouterLink
                              to="/policies/licensing"
                              className="margin-left-xs"
                            >
                              (+ Create New)
                            </RouterLink>
                          )}
                        </ControlLabel>
                        <FormControl
                          componentClass="select"
                          name="default-license-policy"
                          value={defaultPolicyId || ''}
                          onChange={(e) => {
                            // @ts-expect-error This value does, in fact, exist
                            setDefaultLicensingPolicy(e.target.value ? Number(e.target.value) : undefined);
                          }}
                        >
                          <option value="">--- Select a Policy ---</option>
                          {policyOptionsOfType(Category.Licensing)}
                        </FormControl>
                      </InputGroup>
                      <SecurityRequired>
                        <InputGroup
                          className="input-group input-group-md"
                          style={{ marginTop: 20 }}
                        >
                          <ControlLabel className="form-label">
                            Default Security Policy
                            {userHasPermissionToCreatePolicyType(PolicyType.SECURITY) && (
                              <RouterLink
                                to="/policies/security"
                                className="margin-left-xs"
                              >
                                (+ Create New)
                              </RouterLink>
                            )}
                          </ControlLabel>
                          <FormControl
                            componentClass="select"
                            name="default-security-policy"
                            value={defaultSecurityPolicyId || ''}
                            onChange={(e) => {
                              // @ts-expect-error This value does, in fact, exist
                              setDefaultSecurityPolicy(e.target.value ? Number(e.target.value) : undefined);
                            }}
                            >
                            <option value="">--- Select a Policy ---</option>
                            {policyOptionsOfType(Category.Vulnerability)}
                          </FormControl>
                        </InputGroup>
                      </SecurityRequired>
                      <QualityRequired>
                        <InputGroup
                          className="input-group input-group-md"
                          style={{ marginTop: 20 }}
                        >
                          <ControlLabel className="form-label">
                            Default Quality Policy
                            {userHasPermissionToCreatePolicyType(PolicyType.QUALITY) && (
                              <RouterLink
                                to="/policies/quality"
                                className="margin-left-xs"
                              >
                                (+ Create New)
                              </RouterLink>
                            )}
                          </ControlLabel>
                          <FormControl
                            componentClass="select"
                            name="default-quality-policy"
                            value={defaultQualityPolicyId || ''}
                            onChange={(e) => {
                              // @ts-expect-error This value does, in fact, exist
                              setDefaultQualityPolicy(e.target.value ? Number(e.target.value) : undefined);
                            }}
                            >
                            <option value="">--- Select a Policy ---</option>
                            {policyOptionsOfType(Category.Quality)}
                          </FormControl>
                        </InputGroup>
                      </QualityRequired>
                    </div>}
                </FormGroup>
                <br />
                <ButtonGroup spacing="xs">
                  <Button onClick={onHide} type={BUTTON_TYPE.SECONDARY}>Cancel</Button>&nbsp;&nbsp;
                  <InlineLoaderButton
                    onClick={createOrUpdateToken}
                    isLoading={isLoading}
                    isDisabled={isLoading}
                  >
                    {isLoading ? 'Saving' : 'Confirm'}
                  </InlineLoaderButton>
                </ButtonGroup>
              </div>)}
        </div>
      </div>
    </Modal>
  );
};

export default ModalCreateOrEditToken;
